import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

/**
 * Diálogo modal accesible. El ÚNICO sitio donde se resuelve esto, para que cada
 * pantalla no lo reinvente a medias.
 *
 * POR QUÉ EXISTE. Los dos diálogos que había (la hoja de asistencia y la
 * contraseña del kiosco) eran un `<div role="dialog">` y nada más: el foco se
 * quedaba DETRÁS, en el botón que los abrió, así que quien navega con teclado
 * tabulaba por la tabla de debajo sin ver dónde estaba; Escape no hacía nada; y
 * al cerrar, el foco caía al `<body>` y había que volver a recorrer la página
 * entera desde arriba. El de la contraseña además no tenía nombre accesible: el
 * lector de pantalla anunciaba "diálogo" a secas.
 *
 * Lo que hace:
 *   - `role="dialog"`, `aria-modal` y `aria-labelledby` apuntando al título.
 *   - Pone el foco dentro al abrir (el elemento que se pida, o el primero
 *     enfocable, o el propio diálogo).
 *   - Atrapa el tabulador: Tab en el último vuelve al primero y al revés.
 *   - Escape llama a `onCerrar`. Quien pierda algo al cerrar (la contraseña que
 *     solo se ve una vez) decide en su `onCerrar` si pregunta antes.
 *   - Al cerrarse devuelve el foco a donde estaba antes de abrirse.
 *
 * Se pinta en un portal sobre `<body>` para que ningún `overflow` ni `transform`
 * de la pantalla que lo abre lo recorte o lo desplace.
 */

const ENFOCABLES = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function enfocables(raiz: HTMLElement): HTMLElement[] {
  return Array.from(raiz.querySelectorAll<HTMLElement>(ENFOCABLES)).filter(
    // `hidden` y los que están dentro de algo con `display: none` no se pueden
    // enfocar aunque el selector los encuentre. jsdom no calcula el layout, así
    // que se mira el atributo y no `offsetParent`.
    (el) => !el.hasAttribute("hidden") && !el.closest("[hidden]") && el.getAttribute("aria-hidden") !== "true"
  );
}

export function Dialogo({
  tituloId,
  onCerrar,
  children,
  className = "kiosco-dialog",
  claseFondo = "kiosco-dialog-overlay",
  cerrarAlClicFuera = false,
  enfoqueInicial,
}: {
  /** id del encabezado que nombra el diálogo (`aria-labelledby`). Obligatorio: un diálogo sin nombre se anuncia como "diálogo" y nada más. */
  tituloId: string;
  /** Se llama con Escape (y con el clic fuera si `cerrarAlClicFuera`). */
  onCerrar: () => void;
  children: ReactNode;
  className?: string;
  claseFondo?: string;
  /**
   * APAGADO por defecto: un clic accidental fuera del recuadro no debería
   * tirar lo que hay dentro. Se enciende solo donde cerrar no pierde nada.
   */
  cerrarAlClicFuera?: boolean;
  /** Qué recibe el foco al abrir. Sin esto, el primer control; sin controles, el diálogo. */
  enfoqueInicial?: RefObject<HTMLElement>;
}) {
  const caja = useRef<HTMLDivElement>(null);
  // En una ref, no en las dependencias del efecto: quien lo usa suele pasar una
  // función nueva en cada render, y re-ejecutar el efecto movería el foco al
  // principio cada vez que el contenido se actualiza.
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;

  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    const raiz = caja.current;
    if (raiz) {
      const destino = enfoqueInicial?.current ?? enfocables(raiz)[0] ?? raiz;
      destino.focus();
    }
    return () => {
      // Solo si sigue en la página: si lo que abrió el diálogo ya no existe
      // (una fila que se borró), enfocarlo no hace nada útil.
      if (previo && previo.isConnected && typeof previo.focus === "function") previo.focus();
    };
    // Solo al montar: el foco inicial se decide UNA vez, al abrir.
  }, []);

  function alPulsarTecla(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      // Parar aquí: si hay un diálogo dentro de otro, Escape cierra solo el de encima.
      e.stopPropagation();
      e.preventDefault();
      cerrar.current();
      return;
    }
    if (e.key !== "Tab" || !caja.current) return;
    const lista = enfocables(caja.current);
    if (lista.length === 0) {
      // Nada que enfocar dentro: el foco se queda en el diálogo, no se escapa detrás.
      e.preventDefault();
      caja.current.focus();
      return;
    }
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    const activo = document.activeElement;
    if (e.shiftKey && (activo === primero || activo === caja.current)) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && (activo === ultimo || !caja.current.contains(activo))) {
      e.preventDefault();
      primero.focus();
    }
  }

  return createPortal(
    <div
      className={claseFondo}
      role="presentation"
      onClick={cerrarAlClicFuera ? () => cerrar.current() : undefined}
    >
      <div
        ref={caja}
        className={className}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        // -1: enfocable por código (y al hacer clic en una zona sin controles,
        // para que Escape y el tabulador sigan llegando aquí), pero no por Tab.
        tabIndex={-1}
        onKeyDown={alPulsarTecla}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
